import { useState } from 'react'
import { ChevronDown, ChevronRight, Search, UserCheck } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { users as userApi } from '@/api/endpoints'
import type { Role, UserList as AccountPage } from '@/api/types'
import { Err } from '@/components/Err'
import { Pager } from '@/components/Pager'
import { Empty, PageHead, Section, SectionCrumb } from '@/components/layout/Page'
import { ROLE_ORDER, ROLE_SECTION, teamColumn } from '@/domain/words'
import { PAGE_SIZES, peopleChanged, usePage } from '@/hooks/usePage'
import { useSession } from '@/hooks/useSession'
import { AccountChips } from '@/views/user/AccountChips'
import { UserActions } from '@/views/user/UserActions'
import type { Candidate } from '@/views/user/teamPick'
import { opensDetail, personRow, teamsOf } from './model'
import { PeopleTable, type LastColumn } from './PeopleTable'
import { pill } from './pills'
import type { World } from './model'

// 관리자의 1차 축. 한 줄이 한 사람이고, 누가 무엇을 쓰는가만 말한다. 구획이 등급이라 줄에 역할 칸이 없다(F-42) —
// 최고 관리자 · 관리자 · 일반 유저 순으로 보고, 팀 칸은 관리자에게 관리하는 팀, 사용자에게 속한 팀이다.
// 직위 칸은 그 사람의 한도가 온 자리(F-72) — 누가 어느 직위인지 묻는 자리가 이 목록 하나다.
// 조작은 줄이 아니라 제목 줄 오른쪽에 있고 대상은 체크한 사람들이다(F-58) — 규칙은 `views/user/UserActions.tsx` 가 갖는다.
//
// 목록은 서버의 쪽이다(`hooks/usePage.ts`, `GET /users`) — 찾고 세우는 것은 서버이고(역할, 그다음 아이디) 화면은 받은 쪽을
// 받은 순서대로 등급 구획에 나눠 그린다. 구획 제목의 수는 찾기를 따르는 서버의 역할별 수(`counts`)라 이 쪽에 선 줄보다
// 많을 수 있고, 머리의 `n명` 은 찾기와 상관없이 탈퇴하지 않은 모든 사람(`counts.everyone`)이다.
// 검색 칸과 상단 바의 찾기는 서버의 찾는 말 둘이고 둘 다 걸린다 — 무엇으로 찾는지는 스펙의 `listUsers` 가 원본이다.
// 표 머리의 체크박스와 일괄 조작의 대상은 **지금 보이는 쪽의 줄만**이고, 쪽·크기·찾기가 바뀌면 고르기를 비운다.
// 탈퇴한 계정은 등급 구획에 없다 — 맨 아래 접힌 `탈퇴` 구획이 따로 읽는 쪽(`withdrawn=true`)이고 찾기를 같이 따른다.
// 관리자(제한)의 목록에는 탈퇴 계정이 오지 않으므로 그 쪽은 묻지 않는다.
// 관리자의 목록도 모든 사람이다(F-91) — 팀에 넣을 사람의 아이디를 알 길이 여기뿐이다. 줄이 상세를 여는 것은
// 그 사람의 VM·쿼터가 오는 사람뿐이고(`opensDetail`), 나머지 줄은 눌리지 않는다.

// 목록은 변경 알림에 다시 읽고(최고 관리자), 알림이 없는 사이는 이 간격이 메운다 — `World` 의 목록과 같은 간격이다.
const PEOPLE_POLL_MS = 8000
/** 묻지 않는 탈퇴 묶음 — 관리자(제한)에게는 탈퇴 계정이 없다. */
const NO_ONE: AccountPage = { items: [], total: 0 }

export default function UserList({ world, query }: { world: World; query: string }) {
  const me = useSession()
  const top = me.role === 'superadmin'
  const [find, setFind] = useState('')
  const page = usePage({
    read: ({ terms, limit, offset }) => userApi.page({ q: terms, withdrawn: false, limit, offset }),
    // 검색 칸이 먼저, 상단 바의 찾기가 둘째다 — 빈 말은 싣지 않는다.
    terms: [find, query],
    filters: 'live',
    match: peopleChanged,
    pollMs: PEOPLE_POLL_MS,
  })
  const gone = usePage({
    read: ({ terms, limit, offset }) => (top ? userApi.page({ q: terms, withdrawn: true, limit, offset }) : Promise.resolve(NO_ONE)),
    terms: [find, query],
    filters: 'withdrawn',
    match: peopleChanged,
    pollMs: PEOPLE_POLL_MS,
  })
  const [showGone, setShowGone] = useState(false)
  const [picked, setPicked] = useState<string[]>([])
  // 고르기는 지금 쪽의 것이다 — 쪽·크기·찾기가 바뀌면 비운다. 렌더 중에 비우므로 옛 고르기가 새 쪽에 한 번도 서지 않는다.
  const [pickedAt, setPickedAt] = useState(page.at)
  if (pickedAt !== page.at) {
    setPickedAt(page.at)
    setPicked([])
  }

  // 계정 줄은 소속을 싣지 않는다 — 팀 칸은 `World` 의 팀 목록에서 잇는다.
  const rows = (page.data?.items ?? []).map((u) => personRow(u, world.teams))
  const goneRows = (gone.data?.items ?? []).map((u) => personRow(u, world.teams))
  const counts = page.data?.counts
  const goneTotal = gone.data?.total ?? 0
  const chosen = rows.filter((p) => picked.includes(p.user.id))
  const pick = (id: string, on: boolean) =>
    setPicked((v) => (on ? [...v, id] : v.filter((x) => x !== id)))
  // 구획의 전체 선택은 **이 쪽에 보이는** 그 구획의 줄만 본다.
  const pickGroup = (group: Candidate[], on: boolean) =>
    setPicked((v) => {
      const ids = group.map((p) => p.user.id)
      const rest = v.filter((x) => !ids.includes(x))
      return on ? [...rest, ...ids] : rest
    })

  const selectable = top
  const opens = (p: Candidate) => opensDetail(world, me, p.user.id)
  const total = counts?.everyone

  return (
    <>
      <SectionCrumb />
      <PageHead
        icon={<UserCheck className="size-5" />}
        title="사용자"
        afterTitle={
          <div className="relative w-64 max-sm:w-full">
            <Search className="pointer-events-none absolute top-1/2 left-3 size-3.5 -translate-y-1/2 text-muted-foreground" />
            {/* 아이콘과 입력뿐이다 — 검색 아이콘이 있는 칸이 무엇인지는 아이콘이 말한다. */}
            <Input
              value={find}
              onChange={(e) => setFind(e.target.value)}
              aria-label="검색"
              maxLength={100}
              className="h-8 rounded-full bg-background pl-9 text-base sm:text-[13px]"
            />
          </div>
        }
        facts={total === undefined ? undefined : chosen.length > 0 ? `${total}명 · ${chosen.length}명 선택` : `${total}명`}
        right={
          <div className="mt-0.5 flex flex-wrap items-center justify-end gap-1.5">
            <AccountChips />
            <UserActions chosen={chosen} teams={world.teams} onDone={() => setPicked([])} />
          </div>
        }
      />

      <Err error={page.error ?? gone.error} />
      {rows.length === 0 ? (
        // 찾기에 맞는 사람이 탈퇴 구획에만 있으면 그 구획이 말한다.
        goneTotal > 0 || (page.error && !page.data) ? null : (
          <Section title="전체">
            <Empty>{page.loading ? '불러오는 중' : query || find ? '검색 결과 없음' : '사용자 없음'}</Empty>
          </Section>
        )
      ) : (
        <>
          {ROLE_ORDER.map((role) => {
            const group = rows.filter((p) => p.user.role === role)
            if (group.length === 0) return null
            return (
              <Section key={role} title={`${ROLE_SECTION[role]} ${counts?.[role] ?? group.length}`}>
                <PeopleTable
                  group={group}
                  last={teamLast(role)}
                  opens={opens}
                  picking={{
                    selectable,
                    pickable: selectable,
                    picked,
                    onPick: pick,
                    onPickAll: (on) => pickGroup(group, on),
                    allLabel: `${ROLE_SECTION[role]} 전체 선택`,
                  }}
                />
              </Section>
            )
          })}
          <div className="flex flex-wrap items-center gap-2 pt-3">
            <Pager page={page.page} pages={page.pages} onPage={page.setPage} className="mx-0 w-auto flex-1" />
            <div className="flex items-center gap-1.5">
              {PAGE_SIZES.map((n) => (
                <button key={n} type="button" onClick={() => page.setSize(n)} aria-pressed={page.size === n} className={pill(page.size === n)}>
                  {n}명
                </button>
              ))}
            </div>
          </div>
        </>
      )}

      {/* 탈퇴한 계정은 아무것도 쓰지 않는다 — 등급 구획에 섞지 않고 접어 둔다. 펼치면 그 쪽의 쪽 번호가 구획의 이름으로 따로 선다. */}
      {goneTotal > 0 ? (
        <section className="pt-6">
          <button
            type="button"
            onClick={() => setShowGone((v) => !v)}
            aria-expanded={showGone}
            className="mb-2.5 flex items-center gap-1.5 text-[10px] font-semibold tracking-[0.12em] text-muted-foreground uppercase hover:text-foreground"
          >
            {showGone ? <ChevronDown className="size-3.5" /> : <ChevronRight className="size-3.5" />}
            탈퇴 {goneTotal}
          </button>
          {showGone ? (
            <>
              <PeopleTable
                group={goneRows}
                last={teamLast('user')}
                opens={opens}
                picking={{ selectable, pickable: false, picked, onPick: pick }}
              />
              <Pager page={gone.page} pages={gone.pages} onPage={gone.setPage} name="탈퇴" className="pt-3" />
            </>
          ) : null}
        </section>
      ) : null}
    </>
  )
}

// 끝 칸은 팀이다 — 관리자에게는 관리하는 팀, 사용자에게는 속한 팀.
const teamLast = (role: Role): LastColumn => ({
  head: teamColumn(role),
  cell: (p) => teamsOf(p).map((t) => t.display_name).join(', '),
})
